// Drop page: launch time in the store's zone, phases, countdown, hero slideshow and product sheets.
// Pure helpers are exported for tests; the DOM part only runs in a browser.

export const PHASES = Object.freeze(['none', 'upcoming', 'final_day', 'final_hour', 'early_access', 'live', 'sold_out']);
const HOUR = 3600000;

function offsetAt(timestamp, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(timestamp));
  const get = (type) => Number(parts.find((part) => part.type === type)?.value);
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second')) - timestamp;
}

export function parseDropTime(date, time) {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date || '').trim());
  const clock = /^(\d{1,2}):(\d{2})$/.exec(String(time || '00:00').trim());
  if (!day || !clock) return null;
  const [y, m, d, hh, mm] = [day[1], day[2], day[3], clock[1], clock[2]].map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31 || hh > 23 || mm > 59) return null;
  return { y, m, d, hh, mm };
}

// Wall-clock time in `timeZone` to a UTC timestamp. The offset is re-read at the result so DST edges
// settle on one answer (a skipped hour moves forward, a repeated hour takes the first occurrence).
export function zonedToUtc(date, time, timeZone = 'America/New_York') {
  const parts = parseDropTime(date, time);
  if (!parts) return NaN;
  const wall = Date.UTC(parts.y, parts.m - 1, parts.d, parts.hh, parts.mm);
  let guess = wall - offsetAt(wall, timeZone);
  const settled = wall - offsetAt(guess, timeZone);
  if (settled !== guess) guess = Math.min(guess, settled);
  return guess;
}

export function dropPhase(now, start, { earlyMinutes = 0, status = 'auto' } = {}) {
  if (status === 'sold_out' || status === 'live' || status === 'upcoming') return status;
  if (!Number.isFinite(start)) return 'none';
  if (now >= start) return 'live';
  if (earlyMinutes > 0 && now >= start - earlyMinutes * 60000) return 'early_access';
  const left = start - now;
  if (left <= HOUR) return 'final_hour';
  if (left <= 24 * HOUR) return 'final_day';
  return 'upcoming';
}

export function countdownParts(ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
}

const pad = (value) => String(value).padStart(2, '0');

function readConfig(root) {
  try {
    return JSON.parse(root.querySelector('[data-drop-config]')?.textContent || '{}');
  } catch {
    return {};
  }
}

function initCountdown(root) {
  const config = readConfig(root);
  const timer = root.querySelector('[data-drop-countdown]');
  const start = zonedToUtc(config.date, config.time, config.timeZone);
  const phaseOf = () => dropPhase(Date.now(), start, config);
  const setPhase = () => {
    const phase = phaseOf();
    root.dataset.dropPhase = phase;
    return phase;
  };
  setPhase();
  if (!timer || !Number.isFinite(start)) return;

  const units = Object.fromEntries([...timer.querySelectorAll('[data-unit]')].map((el) => [el.dataset.unit, el]));
  const summary = timer.querySelector('[data-drop-summary]');
  const local = root.querySelector('[data-drop-local]');
  if (local) {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (zone && zone !== config.timeZone) {
      local.textContent = `${new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' }).format(start)} ${config.localSuffix || ''}`.trim();
      local.hidden = false;
    }
  }

  let lastMinute = -1;
  let handle = null;
  const tick = () => {
    const parts = countdownParts(start - Date.now());
    for (const [unit, el] of Object.entries(units)) el.textContent = pad(parts[unit]);
    timer.classList.add('is-ready');
    if (summary && parts.minutes !== lastMinute) {
      lastMinute = parts.minutes;
      summary.textContent = `${parts.days}d ${parts.hours}h ${parts.minutes}m`;
    }
    if (setPhase() === 'live') return;
    handle = setTimeout(tick, 1000 - (Date.now() % 1000));
  };
  document.addEventListener('visibilitychange', () => {
    clearTimeout(handle);
    if (!document.hidden) tick();
  });
  tick();
}

const slideVideos = (slide) => [...(slide?.querySelectorAll('video') || [])];
const playVisible = (slide) =>
  slideVideos(slide).forEach((video) => {
    if (video.offsetParent !== null || getComputedStyle(video).display !== 'none') video.play().catch(() => {});
    else video.pause();
  });

function initSlideshow(root) {
  const slides = [...root.querySelectorAll('[data-drop-slide]')];
  const reduced = window.Theme?.motion?.reduced || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (slides.length < 2) {
    if (!reduced) playVisible(slides[0]);
    return;
  }
  const toggle = root.querySelector('[data-drop-pause]');
  const interval = Number(root.dataset.interval || 5) * 1000;
  let index = 0;
  let paused = reduced || root.dataset.autoplay !== 'true';
  let handle = null;
  let hovering = false;

  const show = (next) => {
    index = (next + slides.length) % slides.length;
    slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.inert = !active;
      if (active && !reduced) playVisible(slide);
      else slideVideos(slide).forEach((video) => video.pause());
    });
  };
  const schedule = () => {
    clearTimeout(handle);
    if (!paused && !hovering && !document.hidden) handle = setTimeout(() => (show(index + 1), schedule()), interval);
  };
  const syncToggle = () => {
    if (!toggle) return;
    toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
    toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
  };

  toggle?.addEventListener('click', () => {
    paused = !paused;
    syncToggle();
    schedule();
  });
  root.addEventListener('pointerenter', () => ((hovering = true), schedule()));
  root.addEventListener('pointerleave', () => ((hovering = false), schedule()));
  root.addEventListener('focusin', () => ((hovering = true), schedule()));
  root.addEventListener('focusout', () => ((hovering = false), schedule()));
  document.addEventListener('visibilitychange', schedule);
  if (toggle) toggle.hidden = reduced;
  root.dropShowSlide = (slide) => {
    const target = slides.indexOf(slide);
    if (target < 0) return;
    paused = true;
    syncToggle();
    clearTimeout(handle);
    show(target);
  };
  syncToggle();
  show(0);
  schedule();
}

function initProductSheets(root) {
  root.querySelectorAll('[data-drop-product]').forEach((card) => {
    const dialog = document.getElementById(card.getAttribute('aria-controls'));
    if (!dialog) return;
    card.addEventListener('click', () => {
      const template = dialog.querySelector('template');
      if (template) template.replaceWith(template.content.cloneNode(true));
      dialog.showModal();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-drop-close]')) dialog.close();
    });
    dialog.addEventListener('close', () => card.focus());
  });
}

let openPassword = null;

function initHeader(header) {
  if (header.dataset.dropReady) return;
  header.dataset.dropReady = 'true';
  const dialog = header.querySelector('[data-password-dialog]');
  const opener = header.querySelector('[data-password-open]');
  if (dialog && opener) {
    const open = () => {
      if (!dialog.open) dialog.showModal();
      dialog.querySelector('input[type="password"]')?.focus();
    };
    opener.addEventListener('click', open);
    if (!openPassword) {
      document.addEventListener('click', (event) => {
        if (event.target.closest('[data-drop-open-password]')) openPassword?.();
      });
    }
    openPassword = open;
    dialog.querySelector('[data-password-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => opener.focus());
    if (dialog.dataset.openOnLoad === 'true') open();
  }
  if (header.dataset.mode === 'transparent') {
    const sync = () => header.classList.toggle('is-solid', window.scrollY > 60);
    window.addEventListener('scroll', sync, { passive: true });
    sync();
  }
}

function init(scope = document) {
  scope.querySelectorAll('[data-drop-header]').forEach(initHeader);
  scope.querySelectorAll('[data-drop-hero]').forEach((hero) => {
    if (hero.dataset.dropReady) return;
    hero.dataset.dropReady = 'true';
    initCountdown(hero);
    initSlideshow(hero);
  });
  scope.querySelectorAll('[data-drop-products]').forEach(initProductSheets);
  scope.querySelectorAll('[data-drop-timer]').forEach((timer) => {
    if (timer.dataset.dropReady) return;
    timer.dataset.dropReady = 'true';
    initCountdown(timer);
  });
  scope.querySelectorAll('[data-drop-slideshow]').forEach((slideshow) => {
    if (slideshow.dataset.dropReady) return;
    slideshow.dataset.dropReady = 'true';
    if (slideshow.querySelectorAll('[data-drop-slide]').length > 1) slideshow.classList.add('is-ready');
    initSlideshow(slideshow);
  });
}

if (typeof document !== 'undefined' && !window.__dropInit) {
  window.__dropInit = true;
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-drop-slide]');
    slide?.closest('[data-drop-slideshow], [data-drop-hero]')?.dropShowSlide?.(slide);
  });
}
